/**
 * Abandon a document you started.
 *
 * The rule the business settled on, enforced in the database by
 * rpc_delete_document and only mirrored here:
 *
 *   never submitted  ->  genuinely deleted, because nobody else ever saw it
 *   ever submitted   ->  voided to CANCELLED, because an approver made a
 *                        decision about it and that is part of the record
 *
 * The button therefore has to say which is about to happen — "Delete" and
 * "Cancel this document" are very different promises, and a user who expects
 * one and gets the other stops trusting the screen.
 *
 * Only the creator (or an administrator) sees it, and only while the document
 * is still DRAFT or RETURNED. Everything past that is out of reach: that is
 * what approval means.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { Confirm, useToast } from '@/components/ui'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

export function DeleteDocument({
  docType, docId, status, creatorEmail, creatorId, backTo, onDone, label,
}: {
  docType: 'INDENT' | 'PR' | 'PO' | 'GRN' | 'MIN' | 'MRN' | 'IST'
  docId: string
  status: string
  /**
   * Who raised it. Most documents carry the originator's email; indents only
   * carry the profile id, so either will do.
   */
  creatorEmail?: string | null
  creatorId?: string | null
  /** Where to go once it is gone. */
  backTo: string
  onDone?: () => void
  /** Overrides the button text where the screen needs something shorter. */
  label?: string
}) {
  const { profile } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const mine = (creatorEmail != null && creatorEmail === profile!.email)
    || (creatorId != null && creatorId === profile!.id)
    || profile!.role === 'admin'
  const removable = ['DRAFT', 'RETURNED'].includes(status)
  if (!mine || !removable) return null

  // RETURNED means it went out and came back, so it has certainly been seen.
  const willVoid = status === 'RETURNED'

  return (
    <>
      <button className="btn-danger btn-sm" onClick={() => setOpen(true)}>
        <Trash2 className="h-4 w-4" />
        {label ?? (willVoid ? 'Cancel this document' : 'Delete draft')}
      </button>

      <Confirm
        open={open}
        tone="danger"
        title={willVoid ? `Cancel ${docId}?` : `Delete ${docId}?`}
        confirmLabel={willVoid ? 'Yes, cancel it' : 'Yes, delete it'}
        requireReason={willVoid}
        reasonLabel="Why is it being abandoned? (kept on the record)"
        message={willVoid ? <>
          This document has already been through an approver, so it is <strong>not</strong> deleted.
          It is marked <strong>cancelled</strong>, drops out of the working lists, and its number is
          never reused — anybody asking later what became of {docId} gets an answer.
          <br /><br />
          <strong>This cannot be undone.</strong>
        </> : <>
          This draft was never submitted, so nobody else has seen it. It will be
          <strong> permanently deleted</strong> along with its lines and anything attached to it.
          <br /><br />
          <strong>This cannot be undone.</strong>
        </>}
        onCancel={() => setOpen(false)}
        onConfirm={async (reason) => {
          try {
            const { data, error } = await supabase.rpc('rpc_delete_document', {
              p_doc_type: docType, p_doc_id: docId, p_reason: reason ?? null,
            })
            if (error) throw new Error(error.message)
            setOpen(false)
            toast.success((data as any)?.message ?? `${docId} removed.`)
            onDone?.()
            navigate(backTo)
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not remove the document')
          }
        }}
      />
    </>
  )
}
